import Component from 'vue-class-component';
import { Chrome } from 'vue-color';
import { Mixins, Prop, Watch } from 'vue-property-decorator';
import WhiteboardMenuMixin from '@/core/mixins/whiteboard-menu.mixin';
import {
  BOARD_ANNOTATION_OPTIONS,
  BOARD_EVENTS,
  COLOR_PICKER_ID,
  DEFAULT_COLOR,
  toolIcons,
  ToolIconType,
  toolRanges
} from '@/values/ed-whiteboard.values';
import { ED_CONTEXT_MENU, EdContextMenuModel, EdContextMenuOptionsModel } from '@/plugins/ed-context-menu/ed-context-menu.plugin';
import { COURSE_EVENTS } from '@/content/values/events';
import { CLEAR_SELECTED_TOOLBAR_EVENT, CLOSE_MENU_TOOLS_EVENT } from '@/content/values/ed-presentation-mode.values';
import {_HOMEWORKS_STORE, HOMEWORKS_EVENTS} from '@/homework/values/homeworks.values';


@Component({
  components: {
    'chrome-picker': Chrome
  }
})
export default class EdWhiteboardMenu extends Mixins(WhiteboardMenuMixin) {

  @Prop({required: true}) readonly boardGroupHash: string;
  @Prop({required: false}) readonly currentPageInfo : any;
  @Prop({required: false}) readonly boardPages : any[];
  @Prop({required: false}) readonly customTools: ToolIconType;


  public  isZoom                  : boolean  = true;
  public  isShowTools             : boolean  = false;
  public  isResponsive            : boolean  = false;
  public  toolNameSelected        : string;
  public  isToolSelectedSecondary : boolean;
  private transparencyMenuRef     : string   = 'transparency-menu';
  private clickToolEventType      : string   = 'clickTool';
  private closeMenuIds            : string[] = ['wb-color-picker-library-id', 'wb-color-picker-id', 'menu-custom-colors', 'principal-color'];
  private workInProgressMap       : Map<string, boolean> = new Map();

  private readonly RESPONSIVE_WIDTH_THRESHOLD = 680;

  get offlineMode() : boolean {
    return this.$store.state.AppStore.offlineMode;
  }

  public showColorTool() {
    this.closeOtherRanges();
    this.closeColorPickerMenu();
    this.closeShowToolsMenu();
    this.showToolColors = !this.showToolColors;
  }

  public setColor(key: string): void {
    this.closeColorPickerMenu();
    this.toolbarColorSelected = key;
    this.$edEventBus.$emit(`${BOARD_EVENTS.CHANGE_BOARD_COLOR}-${this.boardGroupHash}`, this.toolColors[key]);
    this.$Courses.Store.setCurrentMenuOptions({key: this.boardGroupHash, value: {color: this.toolColors[key]}});
  }

  public getCurrentColor(): string {
    return this.toolColors[this.toolbarColorSelected];
  }

  public setCurrentColor(): void {
    this.$Courses.Store.setCurrentMenuOptions({key: this.boardGroupHash, value: {color: this.toolbarColorSelected}});
    this.setColor(this.toolbarColorSelected);
  }

  public toolsContextMenu(tool: any , key: string , event: any) {
    if(tool.isDisabled){
      return;
    }
    if(tool.oneTouch) {
      if(this.toolIcons.undo.refId === tool.refId){
        this.callUndoOrRedoOption(BOARD_ANNOTATION_OPTIONS.undo);
        return;
      }
      if(this.toolIcons.redo.refId === tool.refId){
        this.callUndoOrRedoOption(BOARD_ANNOTATION_OPTIONS.redo);
        return;
      }        
      this.callExternalTool(tool.name, tool.isSecondary);
      return;
    }
    if(tool.options) {
      let options = this.setMenuOptions(tool.options);
      // Disable erase options in offline mode
      if (this.offlineMode) {
        options = options.map(option => {
          if (option.id === 'toolID-Eraser-All-Id' || option.id === 'toolID-Eraser-Range-Id') {
            option.disabled = true;
          }
          return option;
        });
      }

      this.$EdContextMenu.show(new EdContextMenuModel({
        position: { x: event.pageX, y: event.pageY },
        options: options,
        className: 'ed-context-menu__white-board--width'
      }))
      .then( (optionSelected = new EdContextMenuOptionsModel({}) ) => {
        if(Object.keys(optionSelected).length === 0 || optionSelected.discartAction) {
          return;
        }
        this.getConfirmationTool(optionSelected, key);
      });
    } else {
      this.activateTool(tool.name , key, false);
    }
  }

  public showRange(key: string): void {
    this.closeColorPickerMenu();
    this.closeColorTool();
    this.closeOtherRanges(key);
    this.toolRanges[key].isShow = !this.toolRanges[key].isShow;
  }

  public setRange(key: string): void {
    this.toolRanges[key].isShow = false;

    if(key === this.toolRanges.chooseSize.refId){
      this.$Courses.Store.setCurrentMenuOptions({key: this.boardGroupHash, value: { size: this.toolRanges[key].value}});
      this.$edEventBus.$emit(`${BOARD_EVENTS.CHANGE_TOOL_SIZE}-${this.boardGroupHash}`, this.toolRanges[key].value);
    } else if(key === this.toolRanges.chooseOpacity.refId) {
      this.$Courses.Store.setCurrentMenuOptions({key: this.boardGroupHash, value: { opacity: this.toolRanges[key].value}});
      this.$edEventBus.$emit(`${BOARD_EVENTS.CHANGE_TOOL_OPACITY}-${this.boardGroupHash}`, this.toolRanges[key].value);
    }
  }

  public setCurrentSizeRange() : void {
    this.setRange(this.toolRanges.chooseSize.refId);
  }

  public getCurrentToolSize() : number {
    return this.toolRanges.chooseSize.value;
  }

  public setCurrentOpacityRange() : void {
    this.setRange(this.toolRanges.chooseOpacity.refId);
  }

  public getCurrentToolOpacity() : number {
    return this.toolRanges.chooseOpacity.value;
  }

  public showTools() : void {
    this.closeColorTool();
    this.closeColorPickerMenu();
    this.isShowTools = !this.isShowTools;
  }

  @Watch('specificColor.hex')
  public colorChanged(newval: string){
    if(!newval){
      return;

    }
    this.toolColors[COLOR_PICKER_ID] = newval;
    this.$Courses.Store.setCurrentMenuOptions({key: this.boardGroupHash, value: { color: newval }});
    return this.$edEventBus.$emit(`${BOARD_EVENTS.CHANGE_BOARD_COLOR}-${this.boardGroupHash}`, newval);
  }

  public resetIcons() : void{
    this.toolIcons = toolIcons();
    this.toolRanges = toolRanges();
    this.toolbarColorSelected = 'chooseColor';
    this.toolNameSelected = this.toolIcons.move.options.hand.name;
    this.isToolSelectedSecondary = false;
    this.addCustomTools();
  }

  public showColorPickerMenu() : void {
    this.closeShowToolsMenu();
    this.closeColorTool();
    this.defineColor()
  }

  public validateUndoOrRedoOption(): void {

    const undoStack: any[] = this.$store.state.HomeworksStore[BOARD_ANNOTATION_OPTIONS.undo][this.boardGroupHash];
    this.toolIcons.undo.isDisabled = !undoStack || undoStack.length === 0 || this.workInProgressMap.size > 0;

    const redoStack: any[] = this.$store.state.HomeworksStore[BOARD_ANNOTATION_OPTIONS.redo][this.boardGroupHash];
    this.toolIcons.redo.isDisabled = !redoStack || redoStack.length === 0 || this.workInProgressMap.size > 0;
  }

  public disableUndoOrRedoOptions(): void {
    this.cleanUndoRedoStacks();
    this.validateUndoOrRedoOption();
  }

  public enableUndoOption() : void {
    this.toolIcons.undo.isDisabled = false;
  }

  public enableRedoOption() : void {
    this.toolIcons.redo.isDisabled = false;
  }

  private callExternalTool(toolName: string, isSecondary: boolean, boardPage: number = 0): void {
    this.closeShowToolsMenu();
    this.$edEventBus.$emit(`${BOARD_EVENTS.CHANGE_TOOGLE_TOOL}-${this.boardGroupHash}`, toolName, isSecondary, boardPage);
  }

  private saveEmptyAnnotatedBoards(boardAnnotationOption: string, boardIds: string[]) {

    let annotatedBoards: any = {};
    boardIds.forEach(boardId => {
      annotatedBoards[boardId] = {};
    });

    this.$store.commit(_HOMEWORKS_STORE.MUTATIONS.UPDATE_BOARD_ANNOTATIONS, { boardAnnotations: boardAnnotationOption, boardGroupHash: this.boardGroupHash, annotatedBoards });
  }

  private callUndoOrRedoOption(boardAnnotations: string) {

    const annotatedBoards: string[] = this.obtainCurrentAnnotatedBoard(boardAnnotations);
    const toolname = BOARD_ANNOTATION_OPTIONS.undo === boardAnnotations ? this.toolIcons.undo.name : this.toolIcons.redo.name;

    if (annotatedBoards && annotatedBoards.length > 0) {

      const contraryStack: string = BOARD_ANNOTATION_OPTIONS.undo === boardAnnotations ? BOARD_ANNOTATION_OPTIONS.redo : BOARD_ANNOTATION_OPTIONS.undo;
      this.saveEmptyAnnotatedBoards(contraryStack, annotatedBoards);

      annotatedBoards.forEach(board => {
        const boardPage = Number.parseInt(board.split('_')[1]);
        this.callExternalTool(toolname, false, boardPage);
      });
    }
  }

  private obtainCurrentAnnotatedBoard(boardAnnotationOption: string): string[] {
    let annotatedBoards = [];

    const boardActionStack = this.$store.state.HomeworksStore[boardAnnotationOption][this.boardGroupHash];
    if (boardActionStack && boardActionStack.length > 0) {
      const boardAction: any = boardActionStack[boardActionStack.length - 1];
      for (const key in boardAction) {
        annotatedBoards.push(key);
      }
    }

    return annotatedBoards;
  }

  private closeColorPickerMenu() : void{
    if(this.showColorPicker){
      this.showColorPicker = false;
    }
  }

  private closeOtherRanges(key?: string) : void {
      for (var toolKey in this.toolRanges) {
        if (!(key && key === toolKey)) {
          this.toolRanges[toolKey].isShow = false;
        }
      }
  }

  private createEraseAllUndoAction(boardPage: number = 0): void {

    if (!this.boardPages && boardPage === 0) {
      return;
    }

    let boardIds: string[] = [];
    const boardIdPrefix: string = this.boardGroupHash + '_';

    // Erase all pages
    if (boardPage === 0) {
      this.boardPages.forEach(page => {
        boardIds.push(boardIdPrefix + page.page);
      });

      // Erase current page
    } else {
      boardIds.push(boardIdPrefix + boardPage);
    }

    this.saveEmptyAnnotatedBoards(BOARD_ANNOTATION_OPTIONS.undo, boardIds);
  }

  private getConfirmationTool(optionSelected: any, key: string) {
    let toolData = {
      toolId: optionSelected.code,
      type: optionSelected.type,
      icon: optionSelected.icon,
      isSecondary: optionSelected.isSecondary || false,
      key
    };
    if(optionSelected.code === this.toolIcons.delete.options.trash.refId) {
      this.createConfirmationModal({
        body: `${this.$i18n.t('homeworks.annotationsDeleteAll')}`,
        action: () => {
          this.createEraseAllUndoAction();
          this.callExternalTool(toolData.type, toolData.isSecondary);
        }
      });
      return;
    } else if(optionSelected.code === this.toolIcons.delete.options.currentPage.refId){
      let currentBoardId : number = this.currentPageInfo ? this.currentPageInfo.boardId : 1;
      this.createConfirmationModal({
        body: `${this.$i18n.t('homeworks.removeCurrentPage')}`,
        action: () => {
          this.createEraseAllUndoAction(currentBoardId);
          this.callExternalTool(this.toolIcons.delete.options.currentPage.name, toolData.isSecondary, currentBoardId);
        }
      });
      return;
    }
    this.setActiveTool(toolData);
  }

  /*private setActiveTool(toolId , key , icon) : void {
    this.activateTool(toolId , key);
    this.toolIcons[key].icon = icon;
  }*/

  private createConfirmationModal({body , action}:
    {body: string , action: Function}) {
    this.$EdModal.createConfirmationModal({
      body,
      action
    })
  }

  public activateTool(toolName: string, key: string, isSecondary: boolean): void {
    this.toolbarSelected = key;
    this.toolNameSelected = toolName;
    this.isToolSelectedSecondary = isSecondary;
    this.$Courses.Store.setCurrentMenuOptions({key: this.boardGroupHash, value: {toolName, 
      isSecondary, boardPage: 0}});
    this.callExternalTool(toolName, isSecondary);
  }

  private setActiveTool(tool: {key , type , icon, isSecondary}) : void {
    if (tool) {
      this.activateTool(tool.type , tool.key, tool.isSecondary);
      this.toolIcons[tool.key].icon = tool.icon;
    }
    else {
      this.activateTool(null , null, null);
    }
  }

  public getActiveTool() : any {
    return {
      key: this.toolbarSelected,
      type: this.toolNameSelected,
      isSecondary: this.isToolSelectedSecondary
    }
  }

  private setDefaultColor(): void{
    this.$Courses.Store.setCurrentMenuOptions({key: this.boardGroupHash, value: {color: DEFAULT_COLOR.FEEDBACK}});
    this.setColor(DEFAULT_COLOR.FEEDBACK);
  }

  private validateMenuStyle(value: boolean) : void {
    this.isZoom = value;
    this.closeShowToolsMenu();
  }

  private closeShowToolsMenu(){
    if(this.isShowTools){
      this.isShowTools = false;
    }
  }
  private closeColorTool() : void{
    if(this.showToolColors){
      this.showToolColors = false;
    }
  }

  private closeMenuElements(){
    this.closeColorTool();
    this.closeColorPickerMenu();
    this.closeOtherRanges();
    this.closeShowToolsMenu();
  }

  private validateMenuSize() : void {
    setTimeout(() => {
      this.$nextTick(() => {
        let menuRef : any = this.$refs[this.transparencyMenuRef];
        this.isResponsive = menuRef?.offsetWidth <= this.RESPONSIVE_WIDTH_THRESHOLD;
      });
    }, 0);
  }

  private createEventClick() : void {
    window.addEventListener('click', this.clickHandler);
  }

  private removeEventClick() : void {
    window.removeEventListener('click', this.clickHandler);
  }

  private clickHandler(event: any) {

    let path = event.path || (event.composedPath && event.composedPath());
    let found = path.filter((el: any) => {
      return this.closeMenuIds.includes(el.id);
    });
    if (found.length === 0) {
      this.closeColorPickerMenu();
      this.closeColorTool();
    }
  }

  private clearSelectedToolbar() : void {
    this.toolbarSelected = '';
  }

  private cleanUndoRedoStacks(): void {
    this.$store.commit(_HOMEWORKS_STORE.MUTATIONS.CLEAR_UNDO_BOARD_ANNOTATIONS, this.boardGroupHash);
    this.$store.commit(_HOMEWORKS_STORE.MUTATIONS.CLEAR_REDO_BOARD_ANNOTATIONS, this.boardGroupHash);
  }

  private updateBoardWorkInProgress(board: string, workInProgress: boolean) {

    if (workInProgress) {
      this.workInProgressMap.set(board, workInProgress);
    } else {
      this.workInProgressMap.delete(board);
    }
    this.toggleUndoRedoUnavailable(this.workInProgressMap.size == 0);
  }


  private toggleUndoRedoUnavailable(enable: boolean) {
    this.toolIcons.redo.isDisabled = !enable;
    this.toolIcons.redo.icon = enable ? 'redo' : 'ban-solid';
    
    this.toolIcons.undo.isDisabled = !enable;
    this.toolIcons.undo.icon = enable ? 'undo' : 'ban-solid';
    this.validateUndoOrRedoOption();
  }

  private addCustomTools() {
    // Merge custom tools with default tools
    this.toolIcons = Object.assign({}, this.toolIcons, this.customTools);
  }


  /**
   * Handler for the resize observer notify event.
   */
  public resizeMenu() {
    this.validateMenuSize();
  }

  private cleanMenuOptions() {
    this.$Courses.Store.setCurrentMenuOptions({key: this.boardGroupHash, value: {}});
  }


  mounted() {   
    this.cleanMenuOptions(); 
    this.addCustomTools();
    this.createEventClick();
    this.setDefaultColor();
    this.$edEventBus.$on(COURSE_EVENTS.CHANGE_PDF_ZOOM, this.validateMenuStyle);
    this.$edEventBus.$on(CLOSE_MENU_TOOLS_EVENT, this.closeMenuElements);
    this.$edEventBus.$on(CLEAR_SELECTED_TOOLBAR_EVENT, this.clearSelectedToolbar);
    this.$edEventBus.$on(BOARD_EVENTS.DRAWING_TASK, this.validateUndoOrRedoOption);
    this.$edEventBus.$on(BOARD_EVENTS.BOARD_WORK_IN_PROGRESS, this.updateBoardWorkInProgress);
    this.$edEventBus.$on(`${HOMEWORKS_EVENTS.CHANGE_SPLIT_TAB}`, this.resizeMenu);
    this.$edEventBus.$on(COURSE_EVENTS.SEND_PRESENTATION_LAYOUT, this.resizeMenu);
    this.$edEventBus.$on(BOARD_EVENTS.PAGE_CONTAINER_CLICKED, this.closeShowToolsMenu);
    window.addEventListener('click', this.setCloseDeployedItems);
    this.validateMenuSize();
  }

  beforeDestroy() {
    this.cleanMenuOptions(); 
    this.cleanUndoRedoStacks();
    this.removeEventClick();
    this.$edEventBus.$off(COURSE_EVENTS.CHANGE_PDF_ZOOM, this.validateMenuStyle);
    this.$edEventBus.$off(CLOSE_MENU_TOOLS_EVENT, this.closeMenuElements);
    this.$edEventBus.$off(CLEAR_SELECTED_TOOLBAR_EVENT, this.clearSelectedToolbar);
    this.$edEventBus.$off(BOARD_EVENTS.DRAWING_TASK, this.validateUndoOrRedoOption);
    this.$edEventBus.$off(BOARD_EVENTS.BOARD_WORK_IN_PROGRESS, this.updateBoardWorkInProgress);
    this.$edEventBus.$off(`${HOMEWORKS_EVENTS.CHANGE_SPLIT_TAB}`, this.resizeMenu);
    this.$edEventBus.$off(COURSE_EVENTS.SEND_PRESENTATION_LAYOUT, this.resizeMenu);
    this.$edEventBus.$off(BOARD_EVENTS.PAGE_CONTAINER_CLICKED, this.closeShowToolsMenu);
    window.removeEventListener('click', this.setCloseDeployedItems);
    this.$EdContextMenu.$emit(ED_CONTEXT_MENU.EVENTS.CLOSE_CONTEXT_MENU);  // Close the context menu if it is open (bug 33046)
  }

  activated() {
    this.validateMenuSize();
  }
}

